import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Shield,
  Save,
  Loader2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";

const SeparatorInline = () => (
  <div className="my-6 h-px w-full bg-border" />
);

const ROLES_PADRAO = [
  { slug: "admin", name: "Administrador" },
  { slug: "supervisor", name: "Supervisor" },
  { slug: "user", name: "Usuário" },
  { slug: "agent", name: "Atendente" },
  { slug: "atendente", name: "Atendente" },
];

const SEPARADOR_LOG = (fn, msg, erro) => {
  if (erro) console.error(`[SettingsIntelligence] ${fn}:`, msg, erro);
  else console.log(`[SettingsIntelligence] ${fn}:`, msg);
};

const SETTING_ENABLED_KEY = "perabitIntelligenceEnabled";
const SETTING_ROLES_KEY = "perabitIntelligenceRoles";
const ROLES_DEFAULT = ["admin", "supervisor"];

const extrairConfigDeAllSettings = (allSettings) => {
  const map = new Map();
  const arr = Array.isArray(allSettings)
    ? allSettings
    : allSettings && typeof allSettings === "object"
    ? Object.values(allSettings)
    : [];
  for (const row of arr) {
    if (row && typeof row === "object" && row.key) {
      map.set(row.key, typeof row.value === "string" ? row.value : String(row.value ?? ""));
    }
  }
  let enabled = false;
  let roles = [...ROLES_DEFAULT];
  try {
    if (map.get(SETTING_ENABLED_KEY) === "true") enabled = true;
    if (map.has(SETTING_ROLES_KEY)) {
      const parsed = JSON.parse(map.get(SETTING_ROLES_KEY) || "null");
      if (Array.isArray(parsed)) roles = parsed.filter((s) => typeof s === "string");
    }
  } catch (e) {
    // mantem default
  }
  return { enabled, roles, found: map.has(SETTING_ENABLED_KEY) || map.has(SETTING_ROLES_KEY) };
};

export default function SettingsIntelligence() {
  const [enabled, setEnabled] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesDisponiveis, setRolesDisponiveis] = useState(ROLES_PADRAO);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [ultimoDiagnostico, setUltimoDiagnostico] = useState(null);

  const salvarViaSettingsFallback = async (enabledVal, rolesArr) => {
    const t1 = {
      key: SETTING_ENABLED_KEY,
      value: enabledVal ? "true" : "false",
      status: null,
      mensagem: null,
    };
    const t2 = {
      key: SETTING_ROLES_KEY,
      value: JSON.stringify(
        Array.isArray(rolesArr)
          ? rolesArr.filter(s => typeof s === "string").slice(0, 100)
          : []
      ),
      status: null,
      mensagem: null,
    };
    const tarefas = [t1, t2];
    for (const t of tarefas) {
      try {
        const r = await api.put(`/settings/${t.key}`, { value: t.value });
        t.status = r?.status ?? 200;
        t.mensagem = "OK";
        SEPARADOR_LOG(
          "salvarViaSettingsFallback",
          `salvo ${t.key} HTTP ${t.status}`
        );
      } catch (err) {
        t.status = err?.response?.status ?? 500;
        const body = err?.response?.data;
        t.mensagem =
          (typeof body === "string" ? body : null) ||
          body?.error ||
          body?.message ||
          err?.message ||
          "sem detalhe";
        SEPARADOR_LOG(
          "salvarViaSettingsFallback",
          `falhou ${t.key} HTTP ${t.status}`,
          err
        );
      }
    }

    let confirmacao = {
      consultou: false,
      enabledBateu: false,
      rolesBateu: false,
    };
    try {
      const conf = await api.get("/settings");
      confirmacao.consultou = true;
      const extraida = extrairConfigDeAllSettings(conf.data);
      confirmacao.enabledBateu = Boolean(extraida.enabled) === Boolean(enabledVal);
      const esperadoRoles = Array.isArray(rolesArr)
        ? rolesArr.filter(s => typeof s === "string").sort().join("|")
        : "";
      const recebidoRoles = Array.isArray(extraida.roles)
        ? extraida.roles.sort().join("|")
        : "";
      confirmacao.rolesBateu = esperadoRoles === recebidoRoles;
    } catch (err) {
      SEPARADOR_LOG("salvarViaSettingsFallback", "confirmacao GET /settings falhou", err);
    }
    const tudoSalvo =
      tarefas.every(t => Number(t.status) >= 200 && Number(t.status) < 300) &&
      confirmacao.consultou &&
      confirmacao.enabledBateu &&
      confirmacao.rolesBateu;

    return {
      tudoSalvo,
      tarefas,
      confirmacao,
    };
  };

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      setErro(null);
      // 1. Tenta a rota oficial nova /intelligence/config. Se der 404 ou
      //    erro por conflito de catch-all de settings, cai no fallback usando
      //    a rota padrão do sistema GET /settings que lista tudo e extrai
      //    as duas chaves que precisamos.
      const [cfgResp, settingsResp, rolesResp] = await Promise.allSettled([
        api.get("/api/intelligence/config"),
        api.get("/settings"),
        api.get("/roles"),
      ]);

      let fallbackCfg = null;
      if (settingsResp.status === "fulfilled" && settingsResp.value?.data) {
        fallbackCfg = extrairConfigDeAllSettings(settingsResp.value.data);
      }

      if (cfgResp.status === "fulfilled" && cfgResp.value?.data) {
        setEnabled(cfgResp.value.data.enabled === true);
        setRoles(Array.isArray(cfgResp.value.data.roles)
          ? [...cfgResp.value.data.roles]
          : fallbackCfg?.roles || [...ROLES_DEFAULT]);
      } else if (fallbackCfg) {
        setEnabled(Boolean(fallbackCfg.enabled));
        setRoles(Array.isArray(fallbackCfg.roles) ? [...fallbackCfg.roles] : [...ROLES_DEFAULT]);
      } else {
        const status = cfgResp.reason?.response?.status;
        if (status === 403) {
          setErro(
            "Você não tem permissão para configurar o Perabit Intelligence. Apenas administradores."
          );
        } else {
          setErro(
            "Não foi possível carregar a configuração. Tente novamente mais tarde."
          );
        }
      }

      if (rolesResp.status === "fulfilled" && rolesResp.value?.data) {
        const lista = Array.isArray(rolesResp.value.data)
          ? rolesResp.value.data
          : [];
        const unicos = new Map();
        for (const r of ROLES_PADRAO) unicos.set(r.slug, r);
        for (const r of lista) {
          if (r?.slug) {
            unicos.set(r.slug, {
              slug: r.slug,
              name: r.name || r.slug,
            });
          }
        }
        setRolesDisponiveis([...unicos.values()]);
      }
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const toggleRole = (slug) => {
    setRoles((prev) =>
      prev.includes(slug) ? prev.filter((r) => r !== slug) : [...prev, slug]
    );
  };

  const salvar = async () => {
    try {
      setSalvando(true);
      setUltimoDiagnostico(null);
      const rolesValido = Array.isArray(roles)
        ? roles.filter((s) => typeof s === "string").slice(0, 100)
        : [...ROLES_DEFAULT];
      const enabledValido = Boolean(enabled);
      const payload = {
        enabled: enabledValido,
        roles: rolesValido,
      };

      let salvou = false;

      try {
        const resposta = await api.put("/api/intelligence/config", payload);
        if (resposta?.data) {
          setEnabled(Boolean(resposta.data.enabled));
          setRoles(Array.isArray(resposta.data.roles) ? [...resposta.data.roles] : []);
        }
        salvou = true;
      } catch (err) {
        const status = err?.response?.status || 500;
        SEPARADOR_LOG("salvar", `fallback necessário: status=${status}`, err);
        if (status === 404) {
          const aviso =
            "⚠️ ROTA /intelligence/config NÃO EXISTE NO BACKEND QUE ESTÁ RODANDO.\n\n" +
            "O código atualizado aqui no frontend/backend/src existe, mas o Node está executando " +
            "um build antigo (pasta backend/dist/) antes das rotas /intelligence serem criadas.\n\n" +
            "Para corrigir, rode NO MESMO AMBIENTE onde o backend está rodando:\n" +
            "  cd backend\n" +
            "  npm run build\n\n" +
            "Depois reinicie o processo do backend e salve novamente.\n\n" +
            "Enquanto isso, o fallback por /settings individual já salvou os valores no banco " +
            "e as configurações estão ativas.";
          toast.error(aviso, { autoClose: 28000 });
        }
      }

      if (!salvou) {
        const resultado = await salvarViaSettingsFallback(enabledValido, rolesValido);
        setUltimoDiagnostico(resultado);
        if (!resultado.tudoSalvo) {
          const detalhes = resultado.tarefas
            .map(t => `${t.key} HTTP ${t.status} (${t.mensagem})`)
            .join(" | ");
          const confirm =
            resultado.confirmacao.consultou
              ? ` confirmação: enabled=${resultado.confirmacao.enabledBateu ? "OK" : "FALHOU"} roles=${resultado.confirmacao.rolesBateu ? "OK" : "FALHOU"}`
              : ` confirmação: (não foi possível ler GET /settings)`;
          throw Object.assign(new Error("fallback não confirmou os valores salvos"), {
            response: {
              status: 422,
              data: {
                error: detalhes + confirm,
              },
            },
          });
        }
        setEnabled(enabledValido);
        setRoles([...rolesValido]);
      }

      toast.success("Configurações do Perabit Intelligence salvas com sucesso.");
    } catch (err) {
      console.error("Salvar config intelligence falhou:", err);
      const errorBody = err?.response?.data;
      const status = err?.response?.status || "500";
      const msg =
        (typeof errorBody === "string" ? errorBody : null) ||
        errorBody?.error ||
        errorBody?.message ||
        `Erro ${status} ao salvar. Detalhe: ver DevTools → Console e Network.`;
      toast.error(msg, { autoClose: 16000 });
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-3 text-sm text-muted-foreground">
          Carregando configuração…
        </span>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-500" />
              <div>
                <p className="font-medium text-foreground">Acesso negado</p>
                <p className="mt-1 text-sm text-muted-foreground">{erro}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary">
          <Sparkles className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Perabit Intelligence
          </h1>
          <p className="text-sm text-muted-foreground">
            Configure quem pode utilizar a inteligência de dados do CRM.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Status do módulo</CardTitle>
          <CardDescription>
            Quando desativado, o menu some do frontend e todas as chamadas à
            API são bloqueadas no backend.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label
                  htmlFor="toggle-intelligence-enabled"
                  className="text-sm font-medium text-foreground"
                >
                  Perabit Intelligence
                </Label>
                <p className="text-xs text-muted-foreground">
                  {enabled
                    ? "Módulo ativo. Usuários autorizados podem consultar os dados."
                    : "Módulo desativado em toda a empresa."}
                </p>
              </div>
            </div>
            <Switch
              id="toggle-intelligence-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Quem pode acessar o Perabit Intelligence?
          </CardTitle>
          <CardDescription>
            Marque os cargos autorizados. A segurança real é validada no
            backend em cada requisição.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {rolesDisponiveis.map((r) => (
            <div
              key={r.slug}
              className="flex items-center gap-3 rounded-md px-3 py-2 transition-colors hover:bg-accent/50"
            >
              <Checkbox
                id={`role-${r.slug}`}
                checked={roles.includes(r.slug)}
                onCheckedChange={() => toggleRole(r.slug)}
              />
              <Label
                htmlFor={`role-${r.slug}`}
                className="cursor-pointer text-sm text-foreground"
              >
                {r.name}
                <span className="ml-2 text-xs text-muted-foreground">
                  ({r.slug})
                </span>
              </Label>
            </div>
          ))}
        </CardContent>
      </Card>

      <SeparatorInline />

      {ultimoDiagnostico && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              Diagnóstico da última tentativa de salvar
            </CardTitle>
            <CardDescription>
              Detalhes do fallback para cada setting e a confirmação de que os
              valores foram salvos mesmo no banco.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <ul className="space-y-1">
              {ultimoDiagnostico.tarefas.map(t => (
                <li key={t.key} className="flex items-center justify-between">
                  <span className="font-mono text-muted-foreground">{t.key}</span>
                  <span className={
                    Number(t.status) >= 200 && Number(t.status) < 300
                      ? "text-emerald-600"
                      : "text-destructive"
                  }>
                    HTTP {t.status} — {t.mensagem}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center justify-between rounded-md bg-muted/40 px-3 py-2">
              <span className="text-muted-foreground">Confirmação pós-save GET /settings:</span>
              <span className={
                ultimoDiagnostico.tudoSalvo
                  ? "text-emerald-600"
                  : "text-destructive"
              }>
                {ultimoDiagnostico.tudoSalvo ? "TUDO CONFIRMADO" : "HÁ FALHAS — DETALHES NO TOAST ACIMA"}
              </span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              enabled bateu:{" "}
              <b className={ultimoDiagnostico.confirmacao.enabledBateu ? "text-emerald-600" : "text-destructive"}>
                {ultimoDiagnostico.confirmacao.enabledBateu ? "SIM" : "NÃO"}
              </b>{" "}
              · roles bateu:{" "}
              <b className={ultimoDiagnostico.confirmacao.rolesBateu ? "text-emerald-600" : "text-destructive"}>
                {ultimoDiagnostico.confirmacao.rolesBateu ? "SIM" : "NÃO"}
              </b>
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={carregar} disabled={salvando}>
          Descartar
        </Button>
        <Button onClick={salvar} disabled={salvando}>
          {salvando ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando…
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Salvar configurações
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
